import { Card, IconButton, LuIcon, Menu, stopPropagation, Text } from "@overmap-ai/blocks"
import type { Issue, IssueType, IssueTypeStatus, Stored } from "@overmap-ai/common"
import { useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { IssueStatusPieChart } from "../Graphs/IssueStatusPieChart"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"

export interface IssueTypeCardProps extends Omit<ComponentProps<typeof Card>, "children" | "onClick"> {
	issueType: Stored<IssueType>
	issueTypeStatuses: Stored<IssueTypeStatus>[]
	issues: Stored<Issue>[]
	onClick?: (issueType: Stored<IssueType>) => void
}

export const IssueTypeCard = memo<IssueTypeCardProps>((props) => {
	const { className, issueType, issues, issueTypeStatuses, onClick, ...rest } = props

	const navigate = useNavigate()

	const handleClick = useCallback(() => {
		if (onClick) onClick(issueType)
	}, [issueType, onClick])

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId/issue-types/",
			to: "/$organizationId/$projectId/issue-types/$issueTypeId",
			params: (prev) => ({ ...prev, issueTypeId: issueType.uuid }),
		})
	}, [issueType.uuid, navigate])

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId/issue-types/",
			to: "/$organizationId/settings/issue-types/$issueTypeId",
			params: (prev) => ({
				...prev,
				issueTypeId: issueType.uuid,
			}),
		})
	}, [issueType.uuid, navigate])

	return (
		<Card variant="surface" className={cx("flex flex-col gap-2", className)} onClick={handleClick} {...rest}>
			<div className="flex h-max w-full">
				<div className="flex h-max w-full items-center justify-between gap-2">
					<div className="flex min-w-0 grow items-center gap-2">
						<IssueTypeIcon issueTypeId={issueType.uuid} />
						<Text className="truncate">{issueType.name}</Text>
						{issues.length > 0 ? (
							<Text size="sm" accentColor="base" weight="regular">
								{issues.length}
							</Text>
						) : null}
					</div>

					<div className="flex items-center gap-2">
						<Menu.Root>
							<Menu.ClickTrigger>
								<IconButton variant="ghost" accentColor="base" size="sm" onClick={stopPropagation}>
									<LuIcon icon={modelIconMapping.more} />
								</IconButton>
							</Menu.ClickTrigger>
							<Menu.Content size="sm">
								<Menu.Item onClick={handleView}>
									<LuIcon icon={modelIconMapping.expand} />
									View
								</Menu.Item>
								<Menu.Item onClick={handleEdit}>
									<LuIcon icon={modelIconMapping.edit} />
									Edit
								</Menu.Item>
							</Menu.Content>
						</Menu.Root>
					</div>
				</div>
			</div>

			<div className="flex size-full items-center justify-center">
				<IssueStatusPieChart
					className="pointer-events-none"
					issues={issues}
					issueTypeStatuses={issueTypeStatuses}
					width="100%"
					height="100%"
					hideTooltip
				/>
			</div>
		</Card>
	)
})
IssueTypeCard.displayName = "IssueTypeCard"
